import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { AppState } from "../../redux/types";
import { formatRegionDetails, formatRegionOption, getPricingRegion, REGIONS } from "../../utils/constants";
import { buildTranslatedPath, getLocaleFromPath } from "../../i18n/config";
import { isPurchaseFlowPath } from "../../utils/purchaseFlow";
import { getDisplayRegion } from "../../utils/regionBootstrap";
import styles from "./LocaleSelector.module.css";


type LocaleSelectorProps = {
    onSelect?: () => void;
    compact?: boolean;
};

const LocaleSelector = ({ onSelect, compact = false }: LocaleSelectorProps) => {
    const regionCode = useSelector((state: AppState) => state.region.code);

    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const { pathname, search } = useLocation();
    const currentLang = getLocaleFromPath(pathname);
    const current = getDisplayRegion(regionCode, pathname);
    const confirmedBuyRegion = sessionStorage.getItem("confirmedBuyRegion");
    const lockedCountry = confirmedBuyRegion && isPurchaseFlowPath(pathname) ? getPricingRegion(confirmedBuyRegion) : null;

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (r: typeof REGIONS[number]) => {
        if (lockedCountry && getPricingRegion(r.code) !== lockedCountry) return;

        setOpen(false);
        onSelect?.();
        const langChanging = r.lang !== currentLang;
        const newPath = langChanging ? buildTranslatedPath(pathname, r.lang, currentLang) + search : undefined;

        // Only dispatch intent — LanguageChangeDialog owns all side effects
        window.dispatchEvent(
            new CustomEvent("regionChangeRequested", {
                detail: { regionCode: r.code, newLang: r.lang, prevLang: currentLang, newPath },
            }),
        );
    };

    return (
        <div className={`${styles.wrapper} ${compact ? styles.compactMobile : ""} locale-selector-widget`} ref={wrapperRef}>
            <button className={styles.trigger} onClick={() => setOpen((o) => !o)}>
                {formatRegionOption(current)} ▾
            </button>
            {open && (
                <div className={styles.menu}>
                    {REGIONS.map((r) => {
                        const countryLocked = !!lockedCountry && getPricingRegion(r.code) !== lockedCountry;
                        return (
                        <button
                            key={r.code}
                            className={`${styles.item}${r.code === regionCode ? ` ${styles.active}` : ""}`}
                            disabled={countryLocked}
                            onClick={() => handleSelect(r)}
                            title={countryLocked ? "Country is locked for this checkout" : undefined}
                        >
                            <span className={styles.itemMain}>{formatRegionOption(r)}</span>
                            <span className={styles.itemSub}>{formatRegionDetails(r)}</span>
                        </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default LocaleSelector;
